<script setup lang="ts">
import { computed, useAttrs } from 'vue';

defineOptions({ inheritAttrs: false, name: 'EGS_Paginator' });
const props = withDefaults(defineProps<{ first?: number; rows?: number; totalRecords?: number; rowsPerPageOptions?: number[]; currentPageReportTemplate?: string }>(), {
	first: 0,
	rows: 10,
	totalRecords: 0,
	rowsPerPageOptions: () => [],
	currentPageReportTemplate: '{first} to {last} of {totalRecords}'
});
const emit = defineEmits<{ page: [event: { page: number; first: number; rows: number; pageCount: number }]; 'update:first': [value: number] }>();
const attrs = useAttrs();
const pageOptions = computed(() => [...new Set([...props.rowsPerPageOptions, props.rows])].sort((a, b) => a - b));
const pageCount = computed(() => Math.max(1, Math.ceil(props.totalRecords / Math.max(1, props.rows))));
const page = computed(() => Math.min(pageCount.value - 1, Math.floor(props.first / Math.max(1, props.rows))));
const pages = computed(() => {
	const count = pageCount.value;
	const start = Math.max(0, Math.min(page.value - 2, count - 5));
	return Array.from({ length: Math.min(5, count) }, (_, index) => start + index);
});
const report = computed(() => {
	const first = props.totalRecords ? props.first + 1 : 0;
	const last = Math.min(props.first + props.rows, props.totalRecords);
	return props.currentPageReportTemplate.replace('{first}', String(first)).replace('{last}', String(last)).replace('{totalRecords}', String(props.totalRecords));
});
function go(nextPage: number, rows = props.rows) {
	const boundedPage = Math.max(0, Math.min(Math.max(0, Math.ceil(props.totalRecords / Math.max(1, rows)) - 1), nextPage));
	const first = boundedPage * rows;
	emit('update:first', first);
	emit('page', { page: boundedPage, first, rows, pageCount: Math.max(1, Math.ceil(props.totalRecords / Math.max(1, rows))) });
}
function changeRows(event: Event) {
	go(0, Number((event.target as HTMLSelectElement).value));
}
</script>

<template>
	<nav
		v-bind="attrs"
		:class="['egs-paginator mt-3 flex w-full flex-wrap items-center justify-center gap-1 border-t border-surface bg-[var(--egs-content-background)] px-2 pb-2 pt-3 text-sm text-color', attrs['class']]"
		aria-label="Table pagination"
	>
		<button type="button" class="egs-paginator-button max-[360px]:hidden" :disabled="page === 0" aria-label="First page" @click="go(0)"><font-awesome-icon icon="angles-left" /></button>
		<button type="button" class="egs-paginator-button" :disabled="page === 0" aria-label="Previous page" @click="go(page - 1)"><font-awesome-icon icon="angle-left" /></button>
		<button
			v-for="pageIndex in pages"
			:key="pageIndex"
			type="button"
			:class="['egs-paginator-button', pageIndex === page ? '!border-primary-600 !bg-primary-600 !text-white' : '']"
			:aria-current="pageIndex === page ? 'page' : false"
			@click="go(pageIndex)"
		>
			{{ pageIndex + 1 }}
		</button>
		<button type="button" class="egs-paginator-button" :disabled="page >= pageCount - 1" aria-label="Next page" @click="go(page + 1)"><font-awesome-icon icon="angle-right" /></button>
		<button type="button" class="egs-paginator-button max-[360px]:hidden" :disabled="page >= pageCount - 1" aria-label="Last page" @click="go(pageCount - 1)"><font-awesome-icon icon="angles-right" /></button>
		<span class="shrink-0 px-2 py-1 text-center text-xs font-medium text-color-secondary">{{ report }}</span>
		<select
			v-if="rowsPerPageOptions.length"
			:value="rows"
			class="h-8 w-[4.25rem] rounded-md border border-surface bg-[var(--egs-content-background)] px-2 text-xs text-color outline-none focus:border-primary"
			aria-label="Rows per page"
			@change="changeRows"
		>
			<option v-for="option in pageOptions" :key="option" :value="option">{{ option }}</option>
		</select>
	</nav>
</template>

<style scoped>
.egs-paginator-button {
	@apply inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-transparent bg-transparent text-color-secondary transition hover:border-surface hover:bg-[var(--egs-content-hover)] hover:text-color focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 disabled:pointer-events-none disabled:opacity-35;
}
</style>
